import { toast } from 'sonner'

import type { HandedOverPrompt } from '@/components/AgentChat/useAgentPanelState'
import type { TextareaSubmitPayload } from '@/components/composer/TextareaWithSubmit'
import { useAgentConversations, useCreateAgentConversation } from '@/hooks/useAgentConversation'
import { parseError } from '@/lib/errors'
import { useAskCaiPanel } from './AskCaiProvider'

/**
 * Hands a first message to the Ask Cai panel and slides it in. Continues the
 * most recent thread; only starts one when there is none. Needs an
 * `AgentKindProvider` of kind ASK_CAI above it, as the conversation hooks do.
 */
export function useAskCaiHandoff() {
  const { activeId, setActiveId, setHandedOverPrompt, open, toggle } = useAskCaiPanel()
  const { conversations } = useAgentConversations()
  const createMutation = useCreateAgentConversation()

  async function handOver(prompt: HandedOverPrompt) {
    try {
      const conversationId =
        activeId || conversations[0]?.id || (await createMutation.mutateAsync(undefined)).id
      setActiveId(conversationId)
      setHandedOverPrompt(prompt)
      if (!open) toggle()
    } catch (error) {
      toast.error(parseError(error))
    }
  }

  /** Sends the message as the first turn. */
  async function send(payload: TextareaSubmitPayload) {
    const { message, documentIds, attachments } = payload
    if (!message && documentIds.length === 0) return
    await handOver({
      text: message,
      documentIds,
      attachments: attachments.map((a) => ({
        documentId: a.documentId,
        attachmentId: a.attachmentId,
        fileName: a.fileName,
        fileExtension: a.fileExtension,
        fileSize: a.fileSize,
        // Without this the just-sent chips stay unclickable all turn.
        fileUrl: a.fileUrl,
      })),
    })
  }

  /** Puts the message in the composer, for the user to add to and send themselves. */
  async function prefill(message: string) {
    await handOver({ text: message, documentIds: [], attachments: [], draft: true })
  }

  return {
    send,
    prefill,
    isPending: createMutation.isPending,
    hasThreads: conversations.length > 0,
    open,
    toggle,
  }
}
